
































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import tournamentStore from '@/modules/tournament/tournament.store'
import { TournamentState } from '@/api/tournament'
import userStore from '@/modules/auth/auth.store'
import layoutStore from '@/modules/shared/layout.store'
import memberStore from '@/modules/member/member.store'
import TournamentBasicInformation from '@/modules/tournament/components/TournamentBasicInformation'
import TournamentActionDialog from '@/modules/tournament/components/TournamentActionDialog'
import { TournamentAction } from '@/modules/tournament/components/TournamentBasicInformation/TournamentBasicInformation.vue'
import gsap, { Power2 } from 'gsap'
import { unixTimestampToDateTime } from '@/utils'
import ServerError from '@/utils/server_error'
import requestLogin from '@/utils/requestLogin'
import { getRealtimeConnection, realtimeEventTarget } from '@/utils/realtime'
import { HubConnection } from '@microsoft/signalr'
import { CancelError, CancellationToken } from '@/utils/cancel'
import TournamentLFGModal from './components/TournamentLFGModal'
import AppButton from '@/modules/shared/components/AppButton'
import { TournamentTeamRole, TournamentTeamState } from '@/api/tournamentTeam'
import { IdentityAPI } from '@/api/identity'
import { ensureTournamentVerification } from '@/utils/verification'
import TournamentRegistrationClosedDialog from '@/modules/tournament/components/TournamentRegistrationClosedDialog/TournamentRegistrationClosedDialog.vue'

export enum OpenMode {
  Slide = 'slide',
  Dim = 'dim'
}

@Component({
  components: {
    TournamentRegistrationClosedDialog,
    TournamentBasicInformation,
    TournamentActionDialog,
    TournamentLFGModal,
    AppButton
  }
})
export default class TournamentLayout extends Vue {
  tournamentModule = getModule(tournamentStore, this.$store)
  userModule = getModule(userStore, this.$store)
  layoutModule = getModule(layoutStore, this.$store)
  memberModule = getModule(memberStore, this.$store)
  tournamentOverlayIsVisible: boolean = false
  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()
  isBackgroundImageError: boolean = false
  loading: boolean = false

  @Prop({ default: OpenMode.Dim }) readonly openMode!: OpenMode

  /* Route parameters */
  get tournamentId() {
    return this.$route.params.tournamentId
  }

  get hasDefaultTab() {
    return !!this.$route.meta.defaultTab
  }

  /* Hooks */
  async created() {
    await this.subscribeRealtimeEvents()
    realtimeEventTarget.addEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.addEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )
  }

  /**
   * This is fired when user exited the tournament view.
   * - Unsubscribe the tournament gracefully using vuex tournamentId.
   * - Destroy vuex tournament data and remove listener
   * [NOTE] this.tournamentId from route is already invalidated in this state.
   */
  async beforeDestroy() {
    let tournamentId = this.tournament!.tournamentId
    this.tournamentModule.resetSelectedTournament()
    this.tournamentModule.resetSelectedTournamentTeams()
    this.tournamentModule.resetSelectedTournamentTeam()
    this.realtimeConnectionCancellationToken.cancel()
    await this.unsubscribeRealtimeEvents(tournamentId)
    realtimeEventTarget.removeEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.removeEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )
  }

  async mounted() {
    await this.fetchData()
    await this.fetchJoinedTournamentTeam()
    setTimeout(() => {
      this.animateTournamentOverlay(this.hasDefaultTab)
    })

    setTimeout(() => {
      if (!this.hasJoinedTournament) {
        this.fetchJoinedTournamentTeam()
      }
    }, 1000)
  }

  /* Vuex State */
  get tournament() {
    return this.tournamentModule.selectedTournament
  }

  get tournamentTeam() {
    return this.tournamentModule.selectedTournamentTeam
  }

  get hasJoinedTournament() {
    return !!this.tournamentModule.selectedTournamentTeam
  }

  get isRequestedToJoin() {
    if (!this.hasJoinedTournament) return false
    return !!this.tournamentModule.selectedTournamentTeam!.players.find(
      player =>
        player.memberId === this.userModule.memberId &&
        player.role === TournamentTeamRole.RequestToJoin
    )
  }

  get hasLoggedIn() {
    return this.userModule.hasLoggedIn
  }

  /* Computed Properties */
  get tournamentBackground() {
    if (
      !this.tournament ||
      !this.tournament.detailBanner ||
      this.isBackgroundImageError
    ) {
      return require('@/assets/default/tournament_background.png')
    }
    return this.$vuetify.breakpoint.mobile
      ? this.tournament.detailMobileBanner
      : this.tournament.detailBanner
  }

  async setSelectedTournament(tournamentId: string) {
    await this.tournamentModule.setSelectedTournament(tournamentId)
  }

  get tournamentAction(): TournamentAction {
    if (this.hasJoinedTournament) {
      return TournamentAction.ViewRegistrationStatus
    }
    return TournamentAction.JoinTournament
  }

  /* Watchers */
  @Watch('hasLoggedIn')
  async onHasLoggedInChanged() {
    await this.fetchJoinedTournamentTeam()
  }

  @Watch('hasDefaultTab')
  async onDefaultTabChanged(hasDefaultTab: boolean) {
    await this.animateTournamentOverlay(hasDefaultTab)
  }

  @Watch('tournamentId')
  async onTournamentIdChanged() {
    this.resetTournamentData()
  }

  /* Methods */
  async fetchData() {
    await this.layoutModule.setProgressBarVisible(true)
    try {
      const tournamentId = this.tournamentId
      await this.setSelectedTournament(tournamentId)

      if (this.tournament && this.tournament.isAutomated) {
        await this.$router.replace({
          name: 'InstantMatchDetails',
          params: {
            tournamentId
          }
        })
      }
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('TournamentLayout.SystemErrorMessage'))
        console.error(err)
      }
    } finally {
      await this.layoutModule.setProgressBarVisible(false)
    }
  }

  /**
   * This is fired when user changed tournament from one to another
   * - Unsubscribe previous tournament using tournamentId from vuex
   * - Destroy vuex tournament data
   * - Subscribe new tournament with tournamentId from route params.
   */
  async resetTournamentData() {
    let tournamentId = this.tournament!.tournamentId
    this.tournamentModule.resetSelectedTournament()
    this.tournamentModule.resetSelectedTournamentTeams()
    this.tournamentModule.resetSelectedTournamentTeam()

    this.realtimeConnectionCancellationToken.cancel()
    await this.unsubscribeRealtimeEvents(tournamentId)

    await this.fetchData()
    await this.fetchJoinedTournamentTeam()
    setTimeout(() => {
      this.animateTournamentOverlay(this.hasDefaultTab)
    })

    // Re-subscribe tournament with new route's tournamentId
    await this.subscribeRealtimeEvents()
  }

  unixToTime(value: number) {
    return unixTimestampToDateTime(value)
  }

  async animateTournamentOverlay(isOpen: boolean) {
    if (isOpen) {
      if (this.openMode === OpenMode.Dim) {
        gsap.to('.detail-wrapper', {
          backgroundColor: 'transparent',
          borderTop: 'none',
          duration: 0
        })
        gsap.to('.tournament-layout-overlay', {
          opacity: 0.75,
          duration: 0.5
        })
      }
      gsap
        .to('.detail-wrapper', {
          height: this.openMode === OpenMode.Dim ? '100%' : '90%',
          duration: 0.5,
          ease: Power2.easeOut
        })
        .then(() => {
          this.tournamentOverlayIsVisible = true
        })
    } else {
      if (this.openMode === OpenMode.Dim) {
        gsap.to('.tournament-layout-overlay', {
          opacity: 0,
          duration: 0.5
        })
      }
      gsap
        .to('.detail-wrapper', {
          height: 0,
          duration: 0.5,
          ease: Power2.easeOut
        })
        .then(() => {
          this.tournamentOverlayIsVisible = false
        })
    }
  }

  onJoinTournament() {
    this.loading = true

    requestLogin(async () => {
      if (
        !(await ensureTournamentVerification({
          tournament: this.tournament!,
          callback: this.onJoinTournament
        }))
      ) {
        this.loading = false
        return
      }

      await this.fetchJoinedTournamentTeam()
      this.setTournamentActionDialogVisible(true)

      this.loading = false
    })
  }

  setTournamentActionDialogVisible(visible: boolean = false) {
    this.tournamentModule.setTournamentActionDialogVisible(visible)
  }

  async fetchJoinedTournamentTeam() {
    try {
      await this.tournamentModule.getMemberJoinedTournamentTeam(
        this.tournamentId
      )
    } catch (err) {
      if (err instanceof ServerError) {
        if (err.getStatusCode() >= 400 && err.getStatusCode() < 500) {
          return
        }

        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('TournamentLayout.SystemErrorMessage'))
        console.error(err)
      }

      await this.$router.push('/')
    }
  }

  async subscribeRealtimeEvents() {
    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(this.$t('TournamentLayout.FailConnectServerMessage'))
      console.error(err)
    }
    await this.realtimeConnection.send('SubscribeTournament', this.tournamentId)
    this.realtimeConnection.on(
      'tournamentStateUpdated',
      this.onTournamentStateUpdated
    )
    this.realtimeConnection.on(
      'tournamentDataUpdated',
      this.onTournamentDataUpdated
    )
    this.realtimeConnection.on(
      'tournamentTeamUpdated',
      this.fetchJoinedTournamentTeam
    )
  }

  async unsubscribeRealtimeEvents(tournamentId: string) {
    if (this.realtimeConnection) {
      this.realtimeConnection.off(
        'tournamentStateUpdated',
        this.onTournamentStateUpdated
      )
      this.realtimeConnection.off(
        'tournamentDataUpdated',
        this.onTournamentDataUpdated
      )
      this.realtimeConnection.off(
        'tournamentTeamUpdated',
        this.fetchJoinedTournamentTeam
      )
      await this.realtimeConnection.send('UnsubscribeTournament', tournamentId)
    }
  }

  async onTournamentStateUpdated(state: TournamentState) {
    const oldState = this.tournament ? this.tournament.state : undefined
    if (
      oldState === TournamentState.OpenRegistration &&
      this.tournamentModule.tournamentActionDialogVisible &&
      (!this.tournamentTeam ||
        this.tournamentTeam.state !== TournamentTeamState.Registered)
    ) {
      await this.tournamentModule.setTournamentActionDialogVisible(false)
      await this.tournamentModule.setTournamentRegistrationClosedDialogVisible(
        true
      )
    }

    if (state === TournamentState.OpenRegistration) {
      if (oldState === TournamentState.WaitingForCheckIn) {
        this.$toast.info(
          this.$t('TournamentLayout.TournamentCheckInReopenRegistration')
        )
      } else {
        this.$toast.info(this.$t('TournamentLayout.TournamentOpenRegistration'))
      }
    } else if (
      state === TournamentState.CompleteRegistration &&
      oldState !== TournamentState.WaitingForCheckIn
    ) {
      this.$toast.info(this.$t('TournamentLayout.TournamentCloseRegistration'))
    }
    await this.fetchData()
  }

  async onTournamentDataUpdated() {
    this.$toast.info(this.$t('TournamentLayout.TournamentInformationUpdated'))
    await this.fetchData()
  }

  async openLFGDialog() {
    try {
      await this.$router.push({
        name: 'TournamentDetailsLFG',
        params: {
          tournamentTeamId: this.tournamentModule.selectedTournamentTeam!
            .tournamentTeamId
        }
      })
    } catch (e) {
      await this.layoutModule.showLFGModal(
        this.tournamentModule.selectedTournamentTeam!.tournamentTeamId
      )
    }
  }

  lookForGroup() {
    requestLogin(async () => {
      await this.fetchJoinedTournamentTeam()
      this.$router.push({ name: 'TournamentDetailsLFG' })
    })
  }

  get TournamentState() {
    return TournamentState
  }

  get hideLFG() {
    if (!this.tournament) return false

    if (this.tournament.isAutomated) return true

    if (this.tournament.capacityMaxPlayersPerTeam === 1) return true

    return false
  }
}
